// Pure helpers behind the moderation queue, the composer status panel and
// the Command Center preflight. Nothing here fetches or renders; finding
// wording lives in moderationFindings.js.
import { categoryWords } from './moderationFindings'
import { checkFailureClass, checkListWords, notEnforcedSentence } from './moderationWords'

const WHITESPACE_RUN_PATTERN = /\s+/g

export const REVIEW_TABS = [
  { value: 'pending', label: 'Needs review', status: 'pending' },
  { value: 'auto_rejected', label: 'Auto-rejected', status: 'auto_rejected' },
  { value: 'decided', label: 'History', status: 'decided' },
]

// reviewTabItems labels the queue tabs with the open counts the list
// returned; History carries none. Without counts (still loading, older API)
// the labels stay bare.
export function reviewTabItems(counts) {
  return REVIEW_TABS.map((entry) => {
    const count = entry.value === 'decided' ? undefined : counts?.[entry.status]
    return { value: entry.value, label: Number.isInteger(count) ? `${entry.label} (${count})` : entry.label }
  })
}

// pendingTabEmpty is the "Needs review" tab's empty state. When nothing
// waits but posts were rejected automatically, it says so, so an
// auto-reject brand's queue never looks empty.
export function pendingTabEmpty(counts) {
  const autoRejected = counts?.auto_rejected || 0
  if (autoRejected > 0) {
    return {
      title: 'Nothing waits for a decision',
      description: `${autoRejected} ${autoRejected === 1 ? 'post was' : 'posts were'} rejected automatically. A brand admin can overturn ${autoRejected === 1 ? 'it' : 'any of them'} from the Auto-rejected tab.`,
      showAutoRejected: true,
    }
  }
  return { title: 'Nothing to review', description: 'Every held post has been decided.', showAutoRejected: false }
}

const REVIEW_STATUS_LABELS = {
  pending: 'Needs review',
  auto_rejected: 'Auto-rejected',
  approved: 'Approved',
  rejected: 'Rejected',
  superseded: 'Superseded',
}

const REVIEW_STATUS_TONES = {
  pending: 'warning',
  auto_rejected: 'error',
  approved: 'success',
  rejected: 'error',
  superseded: 'default',
}

const SOURCE_LABELS = {
  composer: 'Studio composer',
  generated: 'Generated post',
  scheduled: 'Scheduled media',
  import: 'Imported post',
  parlay: 'Parlay',
}

const HOLD_LABELS = {
  provider_pending: 'Waiting for a moderation check',
  provider_unavailable: 'Moderation provider unavailable',
  asset_pending: 'Waiting for media to be checked',
  human_review: 'Held for brand review',
  rejected: 'Rejected by moderation',
}

export function reviewStatusLabel(status) {
  // An unmapped status shows its code; reviews always carry one.
  return REVIEW_STATUS_LABELS[status] || status || ''
}

export function reviewStatusTone(status) {
  return REVIEW_STATUS_TONES[status] || 'default'
}

export function sourceLabel(source) {
  // An unmapped source shows its code; reviews always carry one.
  return SOURCE_LABELS[source] || source || ''
}

export function holdReasonLabel(hold) {
  return HOLD_LABELS[hold] || hold || ''
}

// decisionPayload echoes exactly the versions the reviewer was shown; the
// server refuses a decision against anything newer.
export function decisionPayload(review, reason = '') {
  const payload = {
    expected_request_version: review?.request_version,
    expected_revision: review?.content_revision,
    expected_hash: review?.content_hash,
    expected_policy: review?.policy_fingerprint,
  }
  const trimmed = reason.trim()
  if (trimmed) payload.reason = trimmed
  return payload
}

// reviewDisplayTitle names a review in its panel heading.
export function reviewDisplayTitle(review) {
  return review?.title || review?.excerpt || 'Held post'
}

// reviewShortTitle names a review inside a sentence: at most 80 characters.
export function reviewShortTitle(review) {
  const title = reviewDisplayTitle(review).replace(WHITESPACE_RUN_PATTERN, ' ').trim()
  return title.length <= 80 ? title : `${title.slice(0, 80).trimEnd()}…`
}

// selfApprovalDescription is what a brand admin confirms before approving a
// post they wrote. The decision is allowed and recorded as a self-review.
export function selfApprovalDescription(target) {
  const action = target?.autoRejected ? 'Overturning the automatic rejection of' : 'Approving'
  return `${action} “${target?.title || 'this post'}” publishes it. History records it as a self-review, approved by the person who wrote it.`
}

// carriedApprovalSentence explains an approval recorded automatically.
export function carriedApprovalSentence(review) {
  const origin = review?.carried_from || {}
  const who = origin.decided_by_email || 'a brand admin'
  const at = formatDecidedAt(review?.decided_at)
  const originalAt = formatDecidedAt(origin.decided_at)
  return `Approved automatically${at ? ` on ${at}` : ''}: the text, media and findings are the same as what ${who} approved${originalAt ? ` on ${originalAt}` : ''}.`
}

// previousApprovalSentence introduces what is new on a review whose content
// was approved before.
export function previousApprovalSentence(previous) {
  const who = previous?.decided_by_email || 'A brand admin'
  const at = formatDecidedAt(previous?.decided_at)
  return `${who} approved this post's content${at ? ` on ${at}` : ''}. New since then:`
}

// canOverturn reports whether History offers "Overturn and approve": a
// rejection whose post is unchanged, not being deleted and not expired, as
// the review detail's overturnable says.
export function canOverturn(review) {
  return review?.status === 'rejected' && review.overturnable === true
}

// overturnDescription is what the overturn dialog says it will do.
export function overturnDescription(target) {
  return `“${target?.title || 'This post'}” is approved and can be published again. The author is told, with your reason.`
}

// isOverdue reports a pending review the review SLA escalated.
export function isOverdue(review) {
  return review?.status === 'pending' && Boolean(review?.escalated_at)
}

// reviewQueueLabel is a queue row's accessible name: its title, its status
// and every marker the row shows.
export function reviewQueueLabel(review) {
  const parts = [`Review ${review?.title || review?.excerpt || 'Untitled post'}`, reviewStatusLabel(review?.status)]
  if (isOverdue(review)) parts.push('overdue')
  if (review?.self_review) parts.push('self-review')
  if (review?.carried_from) parts.push('carried over')
  if (review?.overturned) parts.push('overturned')
  return parts.filter(Boolean).join(', ')
}

// POST_DELETING_MESSAGE explains why a review of a post that is pending
// delete cannot be decided; the review returns if the delete is undone.
export const POST_DELETING_MESSAGE = 'This post is being deleted. Undo the deletion to decide its review.'

// SUPERSEDED_MESSAGE explains a review closed by a newer revision.
export const SUPERSEDED_MESSAGE = 'A newer revision of this post replaced this review.'

// Why a rule change closed a review, by its closed_reason. A review closed
// by an edit, a delete, or before the reason was recorded reads
// SUPERSEDED_MESSAGE.
const SUPERSEDED_REASON_MESSAGES = {
  rule_removed: 'Closed because the phrase rule that held this post was removed or changed. The post was checked again under the new rules and released.',
  policy_change: "Closed because this brand's moderation rules changed. The post was checked again under the new rules.",
}

// supersededMessage explains why a superseded review was closed.
export function supersededMessage(review) {
  return SUPERSEDED_REASON_MESSAGES[review?.closed_reason] || SUPERSEDED_MESSAGE
}

export function canDecide(review) {
  return Boolean(review)
    && (review.status === 'pending' || review.status === 'auto_rejected')
    && review.is_current !== false
    && review.post_exists !== false
    && review.post_pending_delete !== true
    && review.post_expired !== true
}

// postExpiredMessage explains why a review of an expired post cannot be
// decided; it returns to the queue when the expiry moves later.
export function postExpiredMessage(expiresAt) {
  const date = expiresAt ? new Date(expiresAt) : null
  const when = date && !Number.isNaN(date.getTime()) ? ` on ${date.toLocaleString()}` : ''
  return `This post expired${when}, so it can no longer be published. Move its expiry date later to decide this review.`
}

// Why a decision was refused, by the stale_reason the review API sends with
// a 409. Each says what happened and that nothing was recorded.
const STALE_MESSAGES = {
  content_changed: 'The post was edited after you opened this review, so your decision was not recorded. The new version is checked on its own.',
  policy_changed: "This brand's moderation rules changed after you opened this review, so your decision was not recorded. Check the refreshed review and decide again.",
  findings_changed: 'What moderation found in this post changed after you opened this review, so your decision was not recorded. Check the refreshed findings and decide again.',
  post_deleting: POST_DELETING_MESSAGE,
  post_deleted: 'This post was deleted, so there is nothing left to decide. The review is kept in History.',
  post_expired: 'This post expired, so it can no longer be published and your decision was not recorded. Move its expiry date later to decide this review.',
}

// An older server sends no stale_reason; an unknown one reads the same.
const STALE_FALLBACK_MESSAGE = 'This post changed since you opened it. The review has been refreshed; check it again before deciding.'

const DECIDED_VERBS = { approved: 'approved', rejected: 'rejected' }

// staleDecisionMessage words a refused decision from its stale_reason. data
// is the 409 body: for already_decided it names who decided, how and when.
export function staleDecisionMessage(reason, data = {}) {
  if (reason === 'already_decided') {
    const who = data?.decided_by_email || 'Another brand admin'
    const verb = DECIDED_VERBS[data?.status] || 'decided'
    const when = formatDecidedAt(data?.decided_at)
    return `${who} already ${verb} this post${when ? ` on ${when}` : ''}. Your decision was not recorded.`
  }
  return STALE_MESSAGES[reason] || STALE_FALLBACK_MESSAGE
}

// The 409 codes of a decision the server refused because the review or its
// post moved on; the page refreshes the review and says why.
const STALE_CODES = new Set(['moderation_review_stale', 'moderation_post_deleting', 'moderation_post_expired'])

const STALE_CODE_REASONS = {
  moderation_post_deleting: 'post_deleting',
  moderation_post_expired: 'post_expired',
}

// classifyDecisionError maps a refused decision onto what the page says. A
// stale refusal refreshes the review, so the panel shows why it changed;
// reason is its stale_reason ('' when the server sent none).
export function classifyDecisionError(error) {
  const status = error?.response?.status
  const data = error?.response?.data || {}
  if (status === 409 && STALE_CODES.has(data.code)) {
    const reason = data.stale_reason || STALE_CODE_REASONS[data.code] || ''
    return {
      kind: 'stale',
      reason,
      message: staleDecisionMessage(reason, data),
      current: data.current || null,
      decidedStatus: data.status || '',
      decidedByEmail: data.decided_by_email || '',
      decidedAt: data.decided_at || '',
    }
  }
  return {
    kind: 'error',
    reason: '',
    message: data.error || error?.message || 'The decision could not be recorded.',
  }
}

// notCurrentMessage explains why an open review cannot be decided right now,
// from the live versions its detail carries: the post was edited, or the
// brand's rules moved and the review is being brought up to date.
export function notCurrentMessage(review) {
  const current = review?.current || {}
  const contentChanged = (current.content_revision != null && current.content_revision !== review?.content_revision)
    || Boolean(current.content_hash && current.content_hash !== review?.content_hash)
  return contentChanged
    ? 'The post was edited after this review was opened. The new version is checked on its own.'
    : "This brand's moderation rules changed after this review was opened. It is being brought up to date; refresh in a moment to decide it."
}

// snapshotFields lists the non-empty text of the immutable snapshot the
// review was opened for, in reading order.
export function snapshotFields(snapshot) {
  if (!snapshot) return []
  const fields = [
    ['Title', snapshot.title],
    ['Subtitle', snapshot.subtitle],
    ['Text', snapshot.text],
    ['Call to action', snapshot.cta_label],
    ['Eyebrow', snapshot.presentation?.eyebrow],
    ['Headline', snapshot.presentation?.headline],
    ['Rationale', snapshot.presentation?.rationale],
    ['Badge', snapshot.presentation?.badge],
  ]
  ;(snapshot.wager_labels || []).forEach((label, index) => {
    fields.push([`Wager label ${index + 1}`, label?.label])
  })
  return fields
    .filter(([, value]) => typeof value === 'string' && value.trim() !== '')
    .map(([label, value]) => ({ label, value }))
}

// formatDecidedAt formats a review date for a sentence ('' when missing or
// unreadable).
export function formatDecidedAt(decidedAt) {
  if (!decidedAt) return ''
  const date = new Date(decidedAt)
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString()
}

// reviewDecisionSentence says who decided a review and when, as far as the
// read model tells this viewer: "admin@brand.com approved it on 9/24/2026,
// 1:06:56 PM." Decision details are only present for viewers who may see
// findings; everyone else reads "a brand admin".
function reviewDecisionSentence(review, verb) {
  const who = review?.decided_by_email || 'a brand admin'
  const when = formatDecidedAt(review?.decided_at)
  return `${who} ${verb} it${when ? ` on ${when}` : ''}.`
}

// flaggedCategories names what a review decided on, for the approved state,
// where the findings are history rather than current problems.
export function flaggedCategories(findings = []) {
  const categories = (findings || []).map((finding) => categoryWords(finding?.category))
  return [...new Set(categories)].join(', ')
}

// Why a moderation job failed for good, in words, by its error_code.
const CHECK_FAILURE_WORDS = {
  provider_misconfigured: 'the provider is misconfigured',
  provider_unavailable: 'the provider was unavailable',
  provider_rate_limited: 'the provider was rate limited',
  provider_malformed: 'the provider sent an answer that could not be read',
  provider_input_rejected: 'the provider refused the content',
  lease_expired: 'the check stopped before it finished',
  scope_unresolved: 'the target regulations are not configured',
  input_missing: 'the media to check was missing',
  video_undecodable: 'the video could not be decoded',
  audio_undecodable: 'the soundtrack could not be decoded',
  provider_result_refused: 'the provider answered with a result Wager refuses',
}

// checkFailureWords says why a moderation check failed, from a job's
// error_code; an unknown code is spelled out, a missing one is ''.
export function checkFailureWords(code) {
  if (!code) return ''
  return CHECK_FAILURE_WORDS[code] || String(code).replaceAll('_', ' ')
}

// What happens to a revision the provider could not check: reconcile
// re-submits it, past the failure cap once the provider answers again or
// an hour after its last failure.
const OUTAGE_RETRY_SENTENCE = 'Moderation tries again automatically, at least once an hour, and Wager can release the post meanwhile.'

// A result the provider should never give (a rejected or blocking audio
// result, which audio's flag-only rule refuses) is a provider fault that
// the same media would repeat, so it is never retried on its own.
const REFUSED_RESULT_SENTENCE = "The moderation provider answered with a result Wager's rules refuse for this check (a rejected or blocking soundtrack result). That is a fault in the provider, not in the post, and it is not retried: Wager has to fix the provider, after which re-uploading the media checks it again. Wager can release the post meanwhile."

// checkFailedView words a failed check by what fixes it
// (moderationWords.checkFailureClass). An outage and a refused result are
// Wager's to release; media and content failures wait on an edit, and a
// refused result on a Wager fix, so none of them is polled; outage and
// scope failures wait on the provider or on Wager, so they are polled
// slowly.
function checkFailedView(code) {
  const words = checkFailureWords(code)
  const view = { tone: 'error', title: 'Moderation check failed', findings: 'current' }
  switch (checkFailureClass(code)) {
    case 'media':
      return {
        ...view,
        description: `Moderation could not check the media in this revision (${words}). Replace the media and save the post to check it again.`,
        poll: false,
      }
    case 'content':
      return {
        ...view,
        description: "The moderation provider refused to check this revision's content. Change the text or media and save the post to check it again.",
        poll: false,
      }
    case 'scope':
      return {
        ...view,
        description: "Moderation is not set up for one of this post's regulations, so it could not be checked. Change the post's regulations, or ask Wager to set them up; it is checked again once they resolve.",
        poll: true,
        pollSlow: true,
      }
    case 'refused':
      return {
        ...view,
        description: REFUSED_RESULT_SENTENCE,
        poll: false,
      }
    default:
      return {
        ...view,
        description: `The moderation provider could not check this revision${words ? ` (${words})` : ''}. ${OUTAGE_RETRY_SENTENCE}`,
        poll: true,
        pollSlow: true,
      }
  }
}

// What a post without a hold means while each switch keeps moderation from
// enforcing it (the read model's not_enforced_reason).
const NOT_ENFORCED_DESCRIPTIONS = {
  platform_off: 'Wager has switched content moderation off for every brand, so nothing is checked, held or released.',
  platform_shadow: 'Wager is running content moderation in observe-only mode: checks are recorded, but nothing is held or released.',
  org_off: 'Moderation is off for this brand, so nothing is checked, held or released.',
}

// unenforced marks the one view that says nothing about the post itself, only
// which switch keeps moderation from enforcing it; book-facing surfaces may
// leave it out.
function notEnforcedView(reason) {
  return {
    tone: 'default',
    title: 'Moderation is not enforced for this post',
    description: NOT_ENFORCED_DESCRIPTIONS[reason] || 'Checks may be recorded, but nothing is held or released yet.',
    poll: false,
    findings: 'current',
    unenforced: true,
  }
}

// A review a brand admin can still decide in the moderation queue.
const OPEN_REVIEW_STATUSES = new Set(['pending', 'auto_rejected'])

// isOpenReview reports whether a read model's review is one a brand admin
// can still decide in the moderation queue (pending or auto_rejected).
export function isOpenReview(review) {
  return OPEN_REVIEW_STATUSES.has(review?.status)
}

// A review hold of a post an operator marked for deletion: the review left
// the queue and comes back if the deletion is undone. The row's deletion
// countdown chip says the rest, so lists show no moderation badge.
const MARKED_FOR_DELETION_VIEW = {
  tone: 'default',
  title: 'Marked for deletion',
  description: 'Its moderation review is paused while the post is marked for deletion. Undo the deletion to send it back to the moderation queue.',
  poll: false,
  badge: false,
  findings: 'current',
}

// A review hold of a post whose expiry passed before anyone decided it.
const EXPIRED_BEFORE_REVIEW_VIEW = {
  tone: 'default',
  title: 'Expired before review',
  description: 'This post expired before a brand admin decided its review, so the review left the moderation queue. Move the expiry date into the future to send it back.',
  poll: false,
  findings: 'current',
}

// notCheckedView describes a post moderation enforces but never checked at
// its current revision: nothing will check it until it is edited (or Wager
// re-checks the brand's live posts), so it never polls. The post read model
// names the exemption it went live under; list rows carry only its type.
function notCheckedView(moderation) {
  const wentLiveWhileOff = moderation.exemption?.type === 'disabled' || moderation.exemption_type === 'disabled'
  if (wentLiveWhileOff) {
    return {
      tone: 'default',
      title: 'Published before moderation, not checked',
      description: 'This revision went live while moderation was off for this brand and has not been checked since. It stays live; editing the post sends it for a check.',
      poll: false,
      findings: 'current',
    }
  }
  return {
    tone: 'default',
    title: 'Not checked by moderation',
    description: 'This revision was saved before moderation was enforced for this brand and has not been checked since. Editing the post sends it for a check.',
    poll: false,
    findings: 'current',
  }
}

// checkIncompleteView describes a required check that covered only part of
// the revision (a soundtrack longer than the transcription cap). It waits on
// Wager, so it is polled slowly.
function checkIncompleteView(moderation) {
  const soundtrack = (moderation.incomplete_checks || []).includes('provider_audio')
  return {
    tone: 'warning',
    title: 'Check incomplete',
    description: soundtrack
      ? 'Moderation could check only the first 30 minutes of the soundtrack. Wager reviews the rest and can release the post; a video under 30 minutes is checked in full.'
      : 'Moderation could check only part of this revision. Wager can review the rest and release the post, or replace the media to check it again.',
    poll: true,
    pollSlow: true,
    findings: 'current',
  }
}

// Added to a release's description when it was granted under an earlier
// policy (the read model's exemption.counts is false).
const STALE_RELEASE_SENTENCE = 'This release was granted under an earlier moderation policy and no longer counts: the post is checked again when it is next evaluated.'

// releasedSentence says who released a revision and when, as far as the
// read model tells this viewer: the granting admin's email is present only
// for viewers who may see findings.
function releasedSentence(exemption) {
  const when = formatDecidedAt(exemption?.granted_at)
  if (exemption?.granted_by_email) return `Released by ${exemption.granted_by_email}${when ? ` on ${when}` : ''}.`
  return when ? `Released on ${when}.` : ''
}

// exemptStatusView describes a revision an exemption lets through: it was
// not checked (or not fully), so it never reads as passed. The words follow
// the exemption the read model names; without one the post only reads as
// released without a full check.
function exemptStatusView(moderation) {
  const exemption = moderation.exemption
  const type = exemption?.type
  if (type === 'grandfathered') {
    return {
      tone: 'default',
      title: 'Published before moderation, not checked',
      description: 'This revision was live before content moderation started for this brand, so it was never checked. Editing the post sends the new revision for a check.',
      poll: false,
      findings: 'current',
    }
  }
  if (type === 'provider_unavailable' || type === 'disabled_release') {
    const checks = exemption.checks_covered || []
    const words = checkListWords(checks)
    const without = words ? `without its ${words} check${checks.length > 1 ? 's' : ''}` : 'without a full check'
    const released = type === 'provider_unavailable'
      ? `Wager released this revision during a moderation provider outage, ${without}.`
      : `Wager released this revision while moderation was off for this brand, ${without}.`
    return {
      tone: 'info',
      title: type === 'provider_unavailable' ? 'Released by Wager without a check' : 'Released by Wager while moderation was off',
      description: [released, releasedSentence(exemption), exemption.counts === false ? STALE_RELEASE_SENTENCE : '']
        .filter(Boolean)
        .join(' '),
      poll: false,
      findings: 'current',
      reason: exemption.reason || '',
    }
  }
  return {
    tone: 'info',
    title: 'Released without a full check',
    description: 'An exemption, not a passed check, lets this revision be published.',
    poll: false,
    findings: 'current',
  }
}

// unenforcedHoldView describes a revision held before a switch stopped
// moderation from enforcing it; reason names that switch. The hold stays: a
// review hold with an open review is still decided in the moderation queue;
// any other hold (a check that never ran, or a review hold left by an edit
// while the switch was off, which has no open review) waits for Wager or
// for moderation to be enforced again. It is null for a post without a hold.
function unenforcedHoldView(hold, review, reason) {
  if (hold === 'human_review' && OPEN_REVIEW_STATUSES.has(review?.status)) {
    return {
      tone: 'warning',
      title: 'Held for brand review',
      description: `${notEnforcedSentence(reason)}, but this revision was held before that. A brand admin can still decide it in the moderation queue.`,
      poll: false,
      findings: 'current',
    }
  }
  if (hold) {
    return {
      tone: 'warning',
      title: 'Held waiting for a check',
      description: `${notEnforcedSentence(reason)}, so this check will not run. Wager can release the post, or it is checked when moderation is enforced again.`,
      poll: false,
      findings: 'current',
    }
  }
  return null
}

// moderationStatusView describes a post's moderation read model for the
// composer. poll is true while an enforced check is still pending or has
// failed or finished incomplete (pollSlow then: it waits on the provider or
// on Wager). badge is false where lists show no moderation chip. The rows
// and their words are the state-to-copy table of the round 2 read model.
// findings says how the panel shows the findings: 'current' lists them as
// what holds or rejects the revision, 'reviewed' as what a brand admin
// approved. reason is a reviewer's recorded reason, when the viewer may see it.
// A post held or rejected before a switch stopped moderation from enforcing
// it still reads as held or rejected; only a post without a hold reads as
// not enforced. The words name that switch (not_enforced_reason).
export function moderationStatusView(moderation) {
  if (!moderation) return null
  const { status, hold_reason: hold, enforced, review } = moderation
  const cleared = status === 'passed' || status === 'exempt'
  const rejected = status === 'rejected' || hold === 'rejected'
  const reviewHold = hold === 'human_review' || status === 'flagged'
  if (moderation.pending_delete && (reviewHold || review?.status === 'auto_rejected')) {
    return { ...MARKED_FOR_DELETION_VIEW }
  }
  if (moderation.expired && !cleared && !rejected && reviewHold) {
    return { ...EXPIRED_BEFORE_REVIEW_VIEW }
  }
  const notEnforcedReason = moderation.not_enforced_reason
  if (!enforced && !rejected) {
    // Wager released this revision while moderation was off: say so, as the
    // not-enforced sentence ("nothing is checked, held or released") would
    // contradict the release that just published it.
    if (status === 'exempt' && moderation.exemption?.type === 'disabled_release') {
      return exemptStatusView(moderation)
    }
    return (!cleared && unenforcedHoldView(hold, review, notEnforcedReason)) || notEnforcedView(notEnforcedReason)
  }
  if (cleared && review?.status === 'approved') {
    return {
      tone: 'success',
      title: 'Approved after brand review',
      description: `Moderation flagged this revision; ${reviewDecisionSentence(review, 'approved')} It may be published.`,
      poll: false,
      findings: 'reviewed',
      reason: review.decision_reason || '',
    }
  }
  if (status === 'exempt') {
    return exemptStatusView(moderation)
  }
  if (status === 'passed') {
    return { tone: 'success', title: 'Passed moderation', description: 'This revision may be published.', poll: false, findings: 'current' }
  }
  if (rejected && review?.status === 'rejected') {
    return {
      tone: 'error',
      title: 'Rejected in brand review',
      description: `Moderation flagged this revision; ${reviewDecisionSentence(review, 'rejected')} It will not be published. Edit the post to submit a new revision.`,
      poll: false,
      findings: 'current',
      reason: review.decision_reason || '',
    }
  }
  if (rejected) {
    const overturnable = review?.status === 'auto_rejected'
    let description = 'This revision will not be published. Edit the post to submit a new revision.'
    if (overturnable) {
      description = 'Moderation flagged this revision and this brand rejects flagged posts automatically. A brand admin can overturn it from the moderation queue.'
    } else if (!enforced) {
      // A rejection carried over from before the switch went off, with no
      // open review to overturn it.
      description = `${notEnforcedSentence(notEnforcedReason)}, but this revision was rejected before that. Wager can release it, or it is checked again when moderation is enforced again.`
    }
    return {
      tone: 'error',
      title: 'Rejected by moderation',
      description,
      poll: false,
      findings: 'current',
    }
  }
  // A post nobody checked has no hold, so no failed check applies to it.
  if (moderation.not_checked) {
    return notCheckedView(moderation)
  }
  // A failed check reads as failed, except on a review hold whose review is
  // still open: a brand admin can still decide it, so the review state shows.
  if (moderation.check_failed && !(hold === 'human_review' && isOpenReview(review))) {
    return checkFailedView(moderation.failure_code)
  }
  if (moderation.check_incomplete) {
    return checkIncompleteView(moderation)
  }
  if (status === 'flagged' || hold === 'human_review') {
    return {
      tone: 'warning',
      title: 'Held for brand review',
      description: 'A brand admin must approve this revision before it is published.',
      poll: false,
      findings: 'current',
    }
  }
  if (hold === 'provider_unavailable') {
    return {
      tone: 'error',
      title: 'Moderation check failed',
      description: `The moderation provider could not check this revision. ${OUTAGE_RETRY_SENTENCE}`,
      poll: true,
      findings: 'current',
    }
  }
  return {
    tone: 'info',
    title: hold === 'asset_pending' ? 'Checking media' : 'Checking content',
    description: holdReasonLabel(hold) || 'This revision is waiting for its moderation checks.',
    poll: true,
    findings: 'current',
  }
}

const BLOCK_MESSAGES = {
  moderation_phrase_blocked: 'This post was not saved: it contains a phrase this brand does not allow.',
  moderation_asset_rejected: 'This post was not saved: it uses media that moderation rejected. Replace the media and save again.',
  moderation_scope_unresolved: 'This post was not saved: its target regulations are not configured for this brand.',
}

// parseModerationBlock reads an enforce-mode refusal of a save (a coded 422).
// field names the public label a phrase refusal is about (tag_label,
// creator_name, cta_target, caption_label), null for the post's own text.
// rejectedAssets lists the media asset ids a moderation_asset_rejected
// refusal names.
export function parseModerationBlock(error) {
  const data = error?.response?.data
  if (error?.response?.status !== 422 || !data?.code || !BLOCK_MESSAGES[data.code]) return null
  return {
    code: data.code,
    message: data.error || BLOCK_MESSAGES[data.code],
    findings: Array.isArray(data.findings) ? data.findings : [],
    unknownTargets: Array.isArray(data.unknown_targets) ? data.unknown_targets : [],
    field: typeof data.field === 'string' ? data.field : null,
    rejectedAssets: Array.isArray(data.rejected_assets) ? data.rejected_assets : [],
  }
}

export function formatAge(seconds) {
  if (seconds == null) return '—'
  if (seconds < 3600) return `${Math.max(1, Math.round(seconds / 60))}m`
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h`
  return `${Math.round(seconds / 86400)}d`
}
